import { ColumnDef } from "./types";
import { getSpreadsheetGridTemplateColumns } from "./gridTemplateTracks";
import { useSpreadsheetColumnResizeContext } from "./SpreadsheetColumnResizeProvider";
import {
    MutableRefObject,
    RefObject,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
} from "react";
import {
    columnResizingFeature,
    columnSizingFeature,
    tableFeatures,
    useTable,
    type ColumnDef as TanStackColumnDef,
    type Header,
    type OnChangeFn,
    type ReactTable,
} from "@tanstack/react-table";

const spreadsheetResizeFeatures = tableFeatures({
    columnSizingFeature,
    columnResizingFeature,
});

type SpreadsheetResizeFeatures = typeof spreadsheetResizeFeatures;

export const MIN_SPREADSHEET_COLUMN_WIDTH_PX = 48;
const EMPTY_RESIZE_ROWS: Array<Record<string, never>> = [];

export function getSpreadsheetColumnResizeId(
    column: ColumnDef,
    columnIndex: number,
): string {
    return column.field !== "" ? column.field : `__col-${columnIndex}`;
}

export function shouldPublishSharedLiveGridTemplate({
    sharedActivelyResizingColumnId,
    localActivelyResizingColumnId,
}: {
    sharedActivelyResizingColumnId: string | null;
    localActivelyResizingColumnId: string | null;
}): boolean {
    return (
        sharedActivelyResizingColumnId === null ||
        localActivelyResizingColumnId !== null
    );
}

export function canResizeSpreadsheetColumn(column: ColumnDef): boolean {
    if (column.spreadsheetRole === "actions") {
        return false;
    }
    const isFixedWidth =
        column.minSize !== undefined &&
        column.maxSize !== undefined &&
        column.minSize === column.maxSize;
    return !isFixedWidth;
}

export function canResizeSpreadsheetHeader(
    header:
        | Header<SpreadsheetResizeFeatures, Record<string, never>, unknown>
        | undefined,
    enableColumnResizing: boolean,
): header is Header<SpreadsheetResizeFeatures, Record<string, never>, unknown> {
    if (!enableColumnResizing || header === undefined) {
        return false;
    }
    const def = header.column.columnDef;
    const isFixedWidth =
        def.minSize !== undefined &&
        def.maxSize !== undefined &&
        def.minSize === def.maxSize;
    return def.id !== "filler" && !isFixedWidth && header.column.getCanResize();
}

export function useSpreadsheetColumnResize({
    columns,
    gridRef,
    defaultGridTemplateColumns,
    enableColumnResizing,
}: {
    columns: ColumnDef[];
    gridRef: RefObject<HTMLDivElement | null>;
    defaultGridTemplateColumns: string;
    enableColumnResizing: boolean;
}): {
    resizeTable: ReactTable<SpreadsheetResizeFeatures, Record<string, never>>;
    resetColumnWidth: (columnIndex: number) => void;
    gridTemplateColumns: string;
    columnSizing: Record<string, number>;
} {
    const {
        columnSizing,
        setColumnSizing,
        userResizedColumnIdsRef: userResizedColumnsRef,
        activelyResizingColumnId,
        claimActivelyResizingColumnId,
        releaseActivelyResizingColumnId,
        setLiveGridTemplateColumns,
        columnLayoutGeneration,
    } = useSpreadsheetColumnResizeContext();
    const lastPublishedActiveColumnIdRef = useRef<string | null>(null);
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const tableRef: MutableRefObject<ReactTable<
        SpreadsheetResizeFeatures,
        Record<string, never>
    > | null> = useRef(null);

    const tanstackColumns = useMemo<
        Array<
            TanStackColumnDef<SpreadsheetResizeFeatures, Record<string, never>>
        >
    >(
        () =>
            columns.map((column, columnIndex) => ({
                id: getSpreadsheetColumnResizeId(column, columnIndex),
                accessorFn: () => undefined,
                minSize: column.minSize ?? MIN_SPREADSHEET_COLUMN_WIDTH_PX,
                maxSize: column.maxSize,
                enableResizing: canResizeSpreadsheetColumn(column),
            })),
        [columns],
    );

    const onColumnSizingChange = useCallback<
        OnChangeFn<Record<string, number>>
    >(
        (updater) => {
            setColumnSizing((prev) => {
                const next =
                    typeof updater === "function" ? updater(prev) : updater;
                Object.keys(next).forEach((columnId) => {
                    if (prev[columnId] !== next[columnId]) {
                        userResizedColumnsRef.current.add(columnId);
                    }
                });
                return next;
            });
        },
        [setColumnSizing, userResizedColumnsRef],
    );

    const resizeTable = useTable({
        features: spreadsheetResizeFeatures,
        data: EMPTY_RESIZE_ROWS,
        columns: tanstackColumns,
        enableColumnResizing,
        defaultColumn: { minSize: MIN_SPREADSHEET_COLUMN_WIDTH_PX },
        state: {
            ...(enableColumnResizing ? { columnSizing } : {}),
        },
        ...(enableColumnResizing && { onColumnSizingChange }),
        ...(enableColumnResizing && { columnResizeMode: "onChange" }),
    });
    tableRef.current = resizeTable;

    const computeColumnSizingFromDOM = useCallback(() => {
        if (!enableColumnResizing) {
            return;
        }
        const headerElements =
            gridRef.current?.querySelectorAll("[data-header-cell]");
        if (!headerElements || headerElements.length === 0) {
            return;
        }
        const nextSizing: Record<string, number> = {};
        columnsRef.current.forEach((column, index) => {
            const columnId = getSpreadsheetColumnResizeId(column, index);
            if (
                !canResizeSpreadsheetColumn(column) ||
                userResizedColumnsRef.current.has(columnId)
            ) {
                return;
            }
            const headerElement = headerElements.item(index);
            if (headerElement instanceof HTMLElement) {
                nextSizing[columnId] = headerElement.offsetWidth;
            }
        });
        if (Object.keys(nextSizing).length === 0) {
            return;
        }
        setColumnSizing((prev) => {
            const hasChanges = Object.entries(nextSizing).some(
                ([columnId, widthPx]) => prev[columnId] !== widthPx,
            );
            return hasChanges ? { ...prev, ...nextSizing } : prev;
        });
    }, [enableColumnResizing, gridRef, setColumnSizing, userResizedColumnsRef]);

    useLayoutEffect(() => {
        computeColumnSizingFromDOM();
    }, [columnLayoutGeneration, computeColumnSizingFromDOM]);

    const columnsLength = columns.length;
    const prevColumnsLengthRef = useRef(columnsLength);
    useLayoutEffect(() => {
        if (columnsLength !== prevColumnsLengthRef.current) {
            prevColumnsLengthRef.current = columnsLength;
            computeColumnSizingFromDOM();
        }
    }, [columnsLength, computeColumnSizingFromDOM]);

    useLayoutEffect(() => {
        const container = gridRef.current;
        if (!container || !enableColumnResizing) {
            return;
        }

        let rafId: number | null = null;
        const observer = new ResizeObserver(() => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const isUserResizingColumn =
                    !!tableRef.current?.state.columnResizing.isResizingColumn;
                if (isUserResizingColumn) {
                    return;
                }
                computeColumnSizingFromDOM();
            });
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [computeColumnSizingFromDOM, enableColumnResizing, gridRef]);

    const resetColumnWidth = useCallback(
        (columnIndex: number) => {
            const column = columns[columnIndex];
            if (!column) {
                return;
            }
            const columnId = getSpreadsheetColumnResizeId(column, columnIndex);
            userResizedColumnsRef.current.delete(columnId);
            setColumnSizing((prev) => {
                const next = { ...prev };
                delete next[columnId];
                return next;
            });
            requestAnimationFrame(() => {
                computeColumnSizingFromDOM();
            });
        },
        [
            columns,
            computeColumnSizingFromDOM,
            setColumnSizing,
            userResizedColumnsRef,
        ],
    );

    const localActivelyResizingColumnId =
        resizeTable.state.columnResizing.isResizingColumn || null;

    useLayoutEffect(() => {
        if (localActivelyResizingColumnId) {
            lastPublishedActiveColumnIdRef.current =
                localActivelyResizingColumnId;
            claimActivelyResizingColumnId(localActivelyResizingColumnId);
        } else {
            const publishedColumnId = lastPublishedActiveColumnIdRef.current;
            if (publishedColumnId) {
                releaseActivelyResizingColumnId(publishedColumnId);
                lastPublishedActiveColumnIdRef.current = null;
            }
        }

        return () => {
            const activeColumnId = lastPublishedActiveColumnIdRef.current;
            if (!activeColumnId) {
                return;
            }
            releaseActivelyResizingColumnId(activeColumnId);
            lastPublishedActiveColumnIdRef.current = null;
        };
    }, [
        claimActivelyResizingColumnId,
        localActivelyResizingColumnId,
        releaseActivelyResizingColumnId,
    ]);

    const columnSizes: Record<string, number> = {};
    resizeTable.getHeaderGroups()[0]?.headers.forEach((header) => {
        columnSizes[header.column.id] = header.column.getSize();
    });

    const gridTemplateColumns = enableColumnResizing
        ? getSpreadsheetGridTemplateColumns({
              defaultGridTemplateColumns,
              columnIds: columns.map(getSpreadsheetColumnResizeId),
              columnSizing: columnSizes,
              userResizedColumnIds: userResizedColumnsRef.current,
              minSize: MIN_SPREADSHEET_COLUMN_WIDTH_PX,
              activelyResizingColumnId,
              columns,
          })
        : defaultGridTemplateColumns;

    useLayoutEffect(() => {
        if (!enableColumnResizing) {
            return;
        }
        if (
            shouldPublishSharedLiveGridTemplate({
                sharedActivelyResizingColumnId: activelyResizingColumnId,
                localActivelyResizingColumnId,
            })
        ) {
            setLiveGridTemplateColumns(gridTemplateColumns);
        }
    }, [
        activelyResizingColumnId,
        enableColumnResizing,
        gridTemplateColumns,
        localActivelyResizingColumnId,
        setLiveGridTemplateColumns,
    ]);

    return {
        resizeTable,
        resetColumnWidth,
        columnSizing,
        gridTemplateColumns,
    };
}
